import React, { useEffect, useState } from 'react';
import { 
  Alert, Box, Button, Card, CardContent, CircularProgress, Divider,
  Grid, Stack, TextField, Typography, InputAdornment, Dialog,
  DialogTitle, DialogContent, DialogActions, Stepper, Step,
  StepLabel, StepContent, IconButton
} from '@mui/material';
import { 
  Security, Email, Phone, QrCode, Lock, CheckCircle, 
  ArrowForward, ArrowBack, ContentCopy
} from '@mui/icons-material';
import axios from 'axios';
import { useAuthHeader, useAuthUser } from '../../Services/Auth/sessionAuth';
import { encryptBody, getMethodLabel, mfaApi, MFA_METHOD, normalizeStatusCode, normalizeMfaPolicy, normalizeContactInfo, parseEncryptedResponse, findMfaQrCode, MfaResolutionResult, logMfaRequest, logMfaSuccess, logMfaError } from './mfaApi';
import Datas from '../../Data/Data.json';
import BgImage from '../../Res/creative-flat-lay-photo-workspace-desk.png';

// HandleMissingContact ne renvoie QUE des indicateurs (HasEmail, HasPhone,
// CanUseMethod) — jamais la valeur réelle du contact. Pour l'email/téléphone
// affichés à l'utilisateur, il faut appeler les endpoints dédiés
// GetEmail / GetPhone (MfaPolicyResolverController), qui lisent directement
// la base de données.
async function fetchUserContact(codUti: string, headers: Record<string, string>) {
  const [emailRes, phoneRes] = await Promise.all([
    axios.get(`${mfaApi.mfaPolicyEmail}?codUti=${codUti}`, { headers, withCredentials: true }),
    axios.get(`${mfaApi.mfaPolicyPhone}?codUti=${codUti}`, { headers, withCredentials: true }),
  ]);
  const emailPayload = logMfaSuccess('GetEmail', emailRes);
  const phonePayload = logMfaSuccess('GetPhone', phoneRes);
const email = emailPayload?.Result?.Email ?? emailPayload?.Result?.email ?? emailPayload?.Email ?? emailPayload?.email ?? '';
  const phoneNumber = phonePayload?.Result?.PhoneNumber ?? phonePayload?.Result?.phoneNumber ?? phonePayload?.PhoneNumber ?? phonePayload?.phoneNumber ?? '';
  return { email, phoneNumber };
}

const OtpInput = ({ length, value, onChange, disabled }: { length: number; value: string; onChange: (v: string) => void; disabled?: boolean }) => {
  const digits = value.split('').slice(0, length);
  while (digits.length < length) digits.push('');
  const refs = React.useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (i: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const char = e.target.value.replace(/\D/g, '').slice(-1);
    const before = value.slice(0, i);
    const after = value.slice(i + 1);
    const newValue = (before + char + after).slice(0, length);
    onChange(newValue);
    if (char && i < length - 1) {
      setTimeout(() => refs.current[i + 1]?.focus(), 0);
    }
  };

  const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      const newValue = value.slice(0, i - 1) + value.slice(i);
      onChange(newValue);
      setTimeout(() => refs.current[i - 1]?.focus(), 0);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const text = e.clipboardData.getData('Text').replace(/\D/g, '').slice(0, length);
    onChange(text);
    refs.current[Math.min(text.length, length - 1)]?.focus();
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
      {Array.from({ length }).map((_, i) => (
        <TextField
          key={i}
          inputRef={(el) => { refs.current[i] = el; }}
          value={digits[i]}
          onChange={(e: any) => handleChange(i, e)}
          onKeyDown={(e: any) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          disabled={disabled}
          inputProps={{ maxLength: 1 }}
          sx={{
            '& .MuiOutlinedInput-root': { width: 48, height: 48, borderRadius: '8px' },
            '& .MuiInputBase-input': { p: 0, textAlign: 'center', fontSize: '1.5rem' },
          }}
        />
      ))}
    </Box>
  );
};

export default function MfaActivationPage() {
  const auth = useAuthUser();
  const authHeader = useAuthHeader();
  const currentUser: any = auth();
  const headers = { Authorization: authHeader(), 'Content-Type': 'application/json' };
  
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);
  
  const [activeStep, setActiveStep] = useState(0);
  const [selectedMethod, setSelectedMethod] = useState<number>(MFA_METHOD.none);
  const [policy, setPolicy] = useState<MfaResolutionResult | null>(null);
  
  // Email et téléphone ne sont JAMAIS saisis par l'utilisateur : ils viennent
  // uniquement du backend (base de données). Ces states sont en lecture
  // seule côté UI, alimentés soit par initializeMfa (policy imposée),
  // soit par handleNext (choix libre de méthode).
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  
  const [qrCodeDialogOpen, setQrCodeDialogOpen] = useState(false);
  const [qrCodeData, setQrCodeData] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [otpCode, setOtpCode] = useState('');

  // Longueur du code OTP — valeur par défaut de secours (6), mise à jour
  // dynamiquement dès que le backend renvoie une longueur explicite
  // (policy résolue, réponse Save, ou réponse OtpSend).
  const [otpLength, setOtpLength] = useState<number>(6);

  // 1. Initialisation : Charger la politique et les données de contact existantes
  useEffect(() => {
    const initializeMfa = async () => {
      setLoading(true);
      try {
        const savedPolicy = sessionStorage.getItem('resolvedMfaPolicy');
        if (savedPolicy) {
          // Le backend sérialise en PascalCase (Method, Source, Reason...) :
          // on normalise systématiquement avant toute utilisation, sinon
          // parsedPolicy.method vaut undefined et casse tous les appels
          // qui en dépendent (ex. HandleMissingContact?requiredMethod=undefined).
          const parsedPolicy = normalizeMfaPolicy(JSON.parse(savedPolicy));
          setPolicy(parsedPolicy);

          // Si la policy porte une longueur explicite (OtpLength / CodeLength / Digits),
          // on l'applique dès maintenant. À adapter selon la clé réelle observée
          // dans le console.log ci-dessus, une fois confirmée côté backend.
          const policyOtpLength = (parsedPolicy as any)?.otpLength ?? (parsedPolicy as any)?.OtpLength ?? (parsedPolicy as any)?.codeLength ?? (parsedPolicy as any)?.CodeLength;
          if (policyOtpLength) {
            setOtpLength(Number(policyOtpLength));
          }
          
          if (parsedPolicy.method !== 0) {
            setSelectedMethod(parsedPolicy.method);
            
            // Récupérer les infos de contact réelles du Backend (endpoints dédiés
            // GetEmail/GetPhone — HandleMissingContact ne renvoie que des booléens)
            logMfaRequest('fetchUserContact (init)', `${mfaApi.mfaPolicyEmail} + ${mfaApi.mfaPolicyPhone}`, { codUti: currentUser?.coduti });
            const contactData = await fetchUserContact(currentUser?.coduti, headers);
            
            // Pré-remplissage avec les données de la banque (lecture seule)
            setEmail(contactData.email);
            setPhone(contactData.phoneNumber);

            // La méthode est résolue côté serveur (MfaPolicyResolver), quelle que soit la
            // source (SOCIETE, AGENCE, GROUPE ou UTILISATEUR) : dès qu'une méthode != None
            // est retournée, elle est imposée et l'utilisateur passe directement à la
            // configuration (Étape 1), sans jamais voir l'écran de libre choix.
            if (parsedPolicy.method !== 0) {
              setActiveStep(1);
            }
          }
        }
      } catch (error) {
        logMfaError('initializeMfa', error);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser?.coduti) {
      initializeMfa();
    }
  }, [currentUser?.coduti]);

  const steps = [
    { label: 'Choisir la méthode', description: 'Sélectionnez votre mode d\'authentification.' },
    { label: 'Configuration', description: 'Vérifiez vos informations de contact.' },
    { label: 'Activation', description: 'Entrez le code de vérification.' }
  ];

  // CORRECTION 1 : passage à l'étape 1 — va chercher le contact réel côté
  // backend AVANT d'afficher l'étape de configuration, y compris quand
  // l'utilisateur a choisi librement sa méthode à l'étape 0 (auparavant,
  // seul le cas "policy imposée" dans initializeMfa faisait cet appel).
  const handleNext = async () => {
    if (activeStep === 0 && selectedMethod !== MFA_METHOD.totp && selectedMethod !== MFA_METHOD.none) {
      try {
        logMfaRequest('fetchUserContact (handleNext)', `${mfaApi.mfaPolicyEmail} + ${mfaApi.mfaPolicyPhone}`, { codUti: currentUser?.coduti });
        const contactData = await fetchUserContact(currentUser?.coduti, headers);
        setEmail(contactData.email);
        setPhone(contactData.phoneNumber);
      } catch (error) {
        logMfaError('handleNext (contact fetch)', error);
      }
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  // 2. Logique de configuration (Validation + Sauvegarde + Envoi OTP)
  async function configureMethod() {
    setBusy(true);
    setMessage(null);
    try {

      // Garde-fou : selectedMethod doit être un MfaMethodType valide (1, 2 ou 3)
      // avant tout appel réseau, pour ne jamais partir avec
      // "requiredMethod=undefined" dans l'URL.
      if (!selectedMethod || !Number.isFinite(selectedMethod) || selectedMethod === MFA_METHOD.none) {
        setMessage({ type: 'error', text: 'Méthode MFA non définie. Veuillez sélectionner une méthode.' });
        setBusy(false);
        return;
      }

      // Vérification des contacts : on se base sur email/phone déjà récupérés
      // via GetEmail/GetPhone (fetchUserContact), pas sur HandleMissingContact —
      // ses indicateurs HasEmail/HasPhone/CanUseMethod se sont montrés
      // incohérents avec la base (HasEmail=false alors que l'email existe en BD).
      // On garde l'appel uniquement à titre de log/diagnostic.
      const checkUrl = `https://localhost:7145/MfaPolicyResolver/HandleMissingContact?codUti=${currentUser?.coduti}&requiredMethod=${selectedMethod}`;
      logMfaRequest('HandleMissingContact (configure, diagnostic)', checkUrl);
      try {
        const checkRes = await axios.get(checkUrl, { headers, withCredentials: true });
        const checkPayload = logMfaSuccess('HandleMissingContact (configure, diagnostic)', checkRes);
      } catch (diagError) {
        logMfaError('HandleMissingContact (configure, diagnostic)', diagError);
      }

      // CORRECTION 3 : la méthode requiert un contact et aucun n'existe côté
      // backend — l'utilisateur ne peut plus le saisir lui-même, on l'invite
      // à contacter l'administrateur au lieu de lui proposer un champ de saisie.
      if (!email && !phone && selectedMethod !== MFA_METHOD.totp) {
        setMessage({ 
          type: 'error', 
          text: `Aucun(e) ${selectedMethod === MFA_METHOD.emailOtp ? 'email' : 'numéro de téléphone'} n'est enregistré pour votre compte. Contactez votre administrateur pour le renseigner.` 
        });
        setBusy(false);
        return;
      }

      if (selectedMethod === MFA_METHOD.totp) {
        // Le stub /Totp/GenerateSecret renvoie toujours une erreur : la génération
        // TOTP (secret + QR code) passe par /UserMfa/GenerateOtp, comme pour les
        // autres méthodes. Le payload attend des clés en minuscule (codUti,
        // methodType) car le contrôleur les lit depuis un objet dynamique.
        const body = { codUti: currentUser?.coduti, methodType: MFA_METHOD.totp };
        logMfaRequest('UserMfa/GenerateOtp (TOTP)', mfaApi.userMfaGenerateOtp, body);
        const response = await axios.post(mfaApi.userMfaGenerateOtp, encryptBody(body), { headers, withCredentials: true });
        const payload = logMfaSuccess('UserMfa/GenerateOtp (TOTP)', response);

        const qrCode = findMfaQrCode(payload);
        const secret = payload?.Result?.Secret ?? payload?.Secret ?? '';

        // Longueur du code TOTP, si le backend la renvoie
        const totpOtpLength = payload?.Result?.OtpLength ?? payload?.OtpLength ?? payload?.Result?.CodeLength ?? payload?.CodeLength;
        if (totpOtpLength) {
          setOtpLength(Number(totpOtpLength));
        }

        if (qrCode) {
          setTotpSecret(secret);
          setQrCodeData(qrCode);
          setQrCodeDialogOpen(true);
        } else {
          setMessage({ type: 'error', text:'Erreur lors de la génération du QR Code TOTP.veillez contacter votre administrateur.' });
        }
      } else {
        // Enregistrement des paramètres MFA (Save)
        const saveBody = {
          CodUti: currentUser?.coduti,
          EmailAddress: email,
          PhoneNumber: phone,
          MfaMethod: selectedMethod,
          Statut: 'PENDING'
        };
        logMfaRequest('UserMfa (Save)', mfaApi.userMfaSave, saveBody);
        const saveResponse = await axios.post(mfaApi.userMfaSave, encryptBody(saveBody), { headers, withCredentials: true });
        const savePayload = logMfaSuccess('UserMfa (Save)', saveResponse);

        // Longueur du code, si le backend la renvoie dès le Save
        const saveOtpLength = savePayload?.Result?.OtpLength ?? savePayload?.OtpLength ?? savePayload?.Result?.CodeLength ?? savePayload?.CodeLength;
        if (saveOtpLength) {
          setOtpLength(Number(saveOtpLength));
        }
        
        if (normalizeStatusCode(savePayload) < 300) {
          // Déclenchement de l'envoi de l'OTP
          const endpoint = selectedMethod === MFA_METHOD.emailOtp ? mfaApi.emailOtpSend : mfaApi.smsOtpSend;
          if (!endpoint) {
           
          }
          const sendBody = { codUti: currentUser?.coduti };
          logMfaRequest('OtpSend', endpoint, sendBody);
          const sendResponse = await axios.post(endpoint, encryptBody(sendBody), { headers, withCredentials: true });

          // CORRECTION : le retour de logMfaSuccess n'était pas capturé — impossible
          // d'inspecter le payload d'envoi (et donc une éventuelle longueur de code).
          const sendPayload = logMfaSuccess('OtpSend', sendResponse);

          // Longueur du code, si le backend la renvoie au moment de l'envoi
          const sendOtpLength = sendPayload?.Result?.OtpLength ?? sendPayload?.OtpLength ?? sendPayload?.Result?.CodeLength ?? sendPayload?.CodeLength;
          if (sendOtpLength) {
            setOtpLength(Number(sendOtpLength));
          }

          if (normalizeStatusCode(sendPayload) >= 300) {
            setMessage({ type: 'error', text:  'Erreur lors de l\'envoi du code.veillez contacter votre administrateur.' });
            return;
          }
          
          setMessage({ type: 'success', text: `Un code a été envoyé à votre ${selectedMethod === MFA_METHOD.emailOtp ? 'Email' : 'Téléphone'}.` });
          setActiveStep(2); // Aller à l'étape de vérification
        } else {
        }
      }
    } catch (error: any) {
      const payload = logMfaError('configureMethod', '');
      setMessage({ type: 'error', text:  'Erreur lors de la configuration.veillez contacter votre administrateur.' });
    } finally {
      setBusy(false);
    }
  }

  // 3. Activation finale
  async function verifyAndActivate() {
    setBusy(true);
    try {
      const body = { CodUti: currentUser?.coduti, OtpCode: otpCode, MethodType: selectedMethod };
      logMfaRequest('UserMfa/VerifyOtp', mfaApi.userMfaVerifyOtp, { ...body, OtpCode: '******' });
      const response = await axios.post(mfaApi.userMfaVerifyOtp, encryptBody(body), { headers, withCredentials: true });
      const payload = logMfaSuccess('UserMfa/VerifyOtp', response);

      if (normalizeStatusCode(payload) < 300) {
        // Activation terminée : on pose le jeton et on redirige vers l'application
        // (même comportement que MfaVerifyPage).
        const token = payload?.Result?.Jeton ?? payload?.Result?.jeton ?? null;
        if (token) {
          document.cookie = `AuthToken=${token}; path=/; secure; samesite=strict`;
        }

        sessionStorage.removeItem('requiresSetup');
        sessionStorage.removeItem('mfaRequired');
        sessionStorage.removeItem('mfaConfigured');
        sessionStorage.removeItem('assignedMfaMethod');

        setMessage({ type: 'success', text: 'MFA activé avec succès ! Redirection en cours...' });
        setTimeout(() => { window.location.replace('/'); }, 1500);
      } else {
        setMessage({ type: 'error', text:'Code de vérification invalide.' });
      }
    } catch (error: any) {
      const payload = logMfaError('verifyAndActivate', error);
      setMessage({ type: 'error', text: 'Erreur lors de la vérification.veillez contacter votre administrateur.' });
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress color="success" />
      </Box>
    );
  }

  return (
    <Box
        className="full"
        sx={{
          height: '100%',
          position: 'absolute',
          overflow: 'hidden',
          alignItems: 'center',
        justifyContent: 'center',
          display: 'flex',
          flexDirection: 'column',
          backgroundImage: `url(${BgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            // background: 'linear-gradient(135deg, rgba(8, 43, 30, 0.88) 0%, rgba(0, 0, 0, 0.72) 100%)',
            zIndex: 0,
          },
        }}
      >
      <Card className="login-card light" sx={{ width: '100%', borderRadius: 3 }}>
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={4}>
            <Box sx={{ textAlign: 'center' }}>
              <Security sx={{ fontSize: 64, color: '#0f9b7e', mb: 1 }} />
              <Typography className="form-title" variant="h4" fontWeight={700} color="#0a6a55">Sécurité MFA</Typography>
              <Typography variant="body2" color="text.secondary">Renforcez la sécurité de votre compte</Typography>
            </Box>

            {message && <Alert severity={message.type} onClose={() => setMessage(null)}>{message.text}</Alert>}

            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel>{step.label}</StepLabel>
                  <StepContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{step.description}</Typography>

                    {/* ÉTAPE 0 : SÉLECTION */}
                    {index === 0 && (
                      <Stack spacing={2}>
                        {policy && policy.method !== 0 ? (
                          <Alert severity="warning" icon={
                            policy.method === MFA_METHOD.emailOtp ? <Email /> :
                            policy.method === MFA_METHOD.smsOtp ? <Phone /> : <QrCode />
                          }>
                            <Typography variant="subtitle2" fontWeight="bold">
                              Méthode imposée{policy.source ? ` par votre ${policy.source}` : ''} : {getMethodLabel(policy.method)}
                            </Typography>
                            {policy.reason && <Typography variant="body2">{policy.reason}</Typography>}
                          </Alert>
                        ) : (
                          <Grid container spacing={2}>
                            {[
                              { id: MFA_METHOD.emailOtp, label: 'Email', icon: <Email /> },
                              { id: MFA_METHOD.smsOtp, label: 'SMS', icon: <Phone /> },
                              { id: MFA_METHOD.totp, label: 'TOTP', icon: <QrCode /> }
                            ].map((m) => (
                              <Grid item xs={12} sm={4} key={m.id}>
                                <Card 
                                  variant="outlined" 
                                  sx={{ 
                                    cursor: 'pointer', 
                                    textAlign: 'center', 
                                    p: 2,
                                    borderColor: selectedMethod === m.id ? '#0f9b7e' : 'inherit',
                                    bgcolor: selectedMethod === m.id ? '#f0f9f6' : 'inherit'
                                  }}
                                  onClick={() => setSelectedMethod(m.id)}
                                >
                                  {m.icon}
                                  <Typography variant="body2" fontWeight={600}>{m.label}</Typography>
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                        )}
                        <Button variant="contained" onClick={handleNext} disabled={selectedMethod === MFA_METHOD.none} sx={{ mt: 2, bgcolor: '#0f9b7e' }}>Suivant</Button>
                      </Stack>
                    )}

                    {/* ÉTAPE 1 : CONFIGURATION — contact backend affiché en LECTURE SEULE,
                        jamais saisi par l'utilisateur */}
                    {index === 1 && (
                      <Stack spacing={3}>
                        {selectedMethod === MFA_METHOD.emailOtp && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            <Email sx={{ color: 'action.active' }} />
                            <Box>
                              <Typography variant="caption" color="text.secondary">Code envoyé à l'adresse email de votre compte</Typography>
                              <Typography variant="body1" fontWeight={600}>
                                {email || 'Aucune adresse email enregistrée pour votre compte'}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        {selectedMethod === MFA_METHOD.smsOtp && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            <Phone sx={{ color: 'action.active' }} />
                            <Box>
                              <Typography variant="caption" color="text.secondary">Code envoyé au numéro de votre compte</Typography>
                              <Typography variant="body1" fontWeight={600}>
                                {phone || 'Aucun numéro de téléphone enregistré pour votre compte'}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        {selectedMethod === MFA_METHOD.totp && (
                          <Alert severity="info">Cliquez sur continuer pour générer votre QR Code de configuration.</Alert>
                        )}
                        
                        <Stack direction="row" spacing={2}>
                          {(!policy || policy.method === 0) && (
                            <Button onClick={handleBack}>Retour</Button>
                          )}
                          <Button 
                            variant="contained" 
                            color="success" 
                            onClick={configureMethod} 
                            disabled={busy || (selectedMethod !== MFA_METHOD.totp && !email && !phone)}
                          >
                            {busy ? <CircularProgress size={24} /> : 'Continuer et envoyer le code'}
                          </Button>
                        </Stack>
                      </Stack>
                    )}

                    {/* ÉTAPE 2 : VÉRIFICATION — maxLength et condition de validation
                        basés sur otpLength (dynamique, alimenté par le backend,
                        avec repli sur 6 par défaut) */}
                    {index === 2 && (
                      <Stack spacing={3}>
                        <OtpInput
                          length={selectedMethod === MFA_METHOD.totp ? 6 : otpLength}
                          value={otpCode}
                          onChange={setOtpCode}
                          disabled={busy}
                        />
                        <Stack direction="row" spacing={2}>
                          <Button onClick={handleBack}>Retour</Button>
                          <Button variant="contained" color="success" onClick={verifyAndActivate} disabled={busy || otpCode.length !== otpLength}>
                            Activer mon MFA
                          </Button>
                        </Stack>
                      </Stack>
                    )}
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </Stack>
        </CardContent>
      </Card>

      {/* Dialog TOTP */}
      <Dialog open={qrCodeDialogOpen} onClose={() => setQrCodeDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 700 }}>Configuration Authenticator</DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          <Typography variant="body2" sx={{ mb: 2 }}>Scannez ce QR Code avec votre application (Google Authenticator, Authy, etc.)</Typography>
          {qrCodeData && <img src={qrCodeData} alt="QR MFA" style={{ width: '100%', maxWidth: '200px' }} />}
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">Secret manuel :</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{totpSecret}</Typography>
            <IconButton size="small" onClick={() => { navigator.clipboard.writeText(totpSecret); setMessage({ type: 'info', text: 'Secret copié' }); }}>
              <ContentCopy fontSize="small" />
            </IconButton>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrCodeDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" color="success" onClick={() => { setQrCodeDialogOpen(false); setActiveStep(2); }}>J'ai scanné le code</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}